import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, FolderPlus } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiError } from "@/lib/api";
import { FolderIcon, FOLDER_COLORS, FOLDER_ICONS, folderColorClasses } from "@/components/folderTheme";

/**
 * R2-Phase-3 — Create folder modal.  Used from /folders page header
 * AND from "+ New folder" inside the AddToFolderDropdown on run detail.
 */
export default function CreateFolderModal({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("indigo");
  const [icon, setIcon] = useState("folder");
  const [visibility, setVisibility] = useState("private");

  function reset() {
    setName("");
    setDescription("");
    setColor("indigo");
    setIcon("folder");
    setVisibility("private");
  }

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post("/folders", {
          name: name.trim(),
          description: description.trim() || null,
          color,
          icon,
          visibility,
        })
      ).data,
    onSuccess: (folder) => {
      toast.success(`Folder "${folder.name}" created`);
      reset();
      onOpenChange(false);
      onCreated?.(folder);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const nameOk = name.trim().length >= 3;
  const palette = folderColorClasses(color);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md" data-testid="create-folder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FolderPlus size={16} className="text-indigo-600" />
            New folder
          </DialogTitle>
          <DialogDescription>
            Group demo runs into a collection — useful for pitch decks,
            internal training, or weekly review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="folder-name" className="text-xs font-semibold">
              Name
            </Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. CFO Pitch Decks"
              maxLength={80}
              data-testid="folder-name-input"
              autoFocus
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="folder-desc" className="text-xs font-semibold">
              Description <span className="text-slate-400">(optional)</span>
            </Label>
            <Textarea
              id="folder-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What goes here?"
              maxLength={500}
              rows={2}
              data-testid="folder-desc-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Icon</Label>
              <div className="flex flex-wrap gap-1">
                {FOLDER_ICONS.map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    onClick={() => setIcon(opt)}
                    className={[
                      "grid h-8 w-8 place-items-center rounded-md border",
                      icon === opt
                        ? `${palette.border} ${palette.bg} ${palette.text}`
                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
                    ].join(" ")}
                    data-testid={`folder-icon-${opt}`}
                  >
                    <FolderIcon name={opt} size={14} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Colour</Label>
              <div className="flex flex-wrap gap-1">
                {FOLDER_COLORS.map((opt) => {
                  const p = folderColorClasses(opt);
                  return (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => setColor(opt)}
                      title={opt}
                      className={[
                        "h-7 w-7 rounded-md border",
                        p.bg,
                        color === opt ? "ring-2 ring-slate-900 ring-offset-2" : p.border,
                      ].join(" ")}
                      data-testid={`folder-color-${opt}`}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-semibold">Visibility</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="h-9 text-xs" data-testid="folder-visibility-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private" data-testid="folder-visibility-private">
                  Private — only you (and admins) see it
                </SelectItem>
                <SelectItem value="org" data-testid="folder-visibility-org">
                  Org — anyone at eyvo.com can see &amp; add runs
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
            data-testid="folder-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!nameOk || createMutation.isPending}
            data-testid="folder-modal-create"
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700"
          >
            {createMutation.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <FolderPlus size={13} />
            )}
            Create folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
